import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";

import * as firebase from "firebase/app";
import "firebase/firestore";
import { WhereList } from "../models/pagination";
import QnA, { QnAReply } from "../models/qna.model";
import { environment } from "../../environments/environment";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class QnAService {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl;
  userLogin: User;
  private dbPath = "/qnas";

  qnasRef: AngularFirestoreCollection<QnA>;
  replysRef: AngularFirestoreCollection<QnAReply>;

  constructor(
    private http: HttpClient,
    private db: AngularFirestore,
    private userStore: UserService
  ) {
    this.qnasRef = db.collection(this.dbPath);

    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  getTopPublishedQnAs(timestamp?: number): AngularFirestoreCollection<QnA> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("privacy", "==", false)
        // .where("timestamp", ">=", timestamp)
        // .orderBy("timestamp", "desc")
        .orderBy("noLiked", "desc")
        .limit(10)
    );
  }

  getPublishedQnAs(uid: string, limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("privacy", "==", false);

    if (uid != "") {
      ref = ref.where("createdBy", "==", uid);
    }

    ref = ref.orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  getPublishedByAuthor(uid: string): AngularFirestoreCollection<QnA> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("privacy", "==", false)
        .where("createdBy", "==", uid)
        .orderBy("createdOn", "desc")
        .limit(5)
    );
  }

  getQnAsSameTag(tag: string): AngularFirestoreCollection<QnA> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("privacy", "==", false)
        .where("tags", "array-contains", tag)
        .orderBy("createdOn", "desc")
        .limit(2)
    );
  }

  getQnAs(wheres: WhereList[], limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true);

    for (var i = 0; i < wheres.length; i++) {
      ref = ref.where(wheres[i].fieldPath, "==", wheres[i].value);
    }

    ref = ref.orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  getMyQnAs(uid: string, limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("createdBy", "==", uid)
      .orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  getQnA(id: string) {
    return this.qnasRef.doc(id).valueChanges();
  }

  getById(id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath).doc(id);

    return ref.get();
  }

  createQnA(qna: QnA): any {
    qna.privacy = qna.privacy ? qna.privacy : false;
    qna.status = qna.status ? qna.status : 0;
    qna.isActive = true;
    qna.isDeleted = false;
    qna.createdOn = new Date();
    qna.createdBy = this.userLogin.uid;
    qna.updatedOn = new Date();
    qna.updatedBy = this.userLogin.uid;
    qna.author = this.userLogin;
    qna.visibility = this.visibility;

    qna.noReply = 0;
    qna.noLiked = 0;

    return this.qnasRef.add({ ...qna }).then((docRef) => {
      qna.id = docRef.id;
      const syncData = { id: qna.id, ...qna };
      this.sync(syncData);

      return qna;
    });
  }

  updateQnA(qna: QnA): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    return this.qnasRef
      .doc(qna.id)
      .update({
        title: qna.title,
        description: qna.description,
        sourceType: qna.sourceType,
        content: qna.content ? qna.content : "",
        videoUrl: qna.videoUrl ? qna.videoUrl : "",
        imagesUrl: qna.imagesUrl ? qna.imagesUrl : [],
        tags: qna.tags,
        status: qna.status ? qna.status : 0,
        updatedOn: updatedOn,
        updatedBy: updatedBy,
        author: this.userLogin,
      })
      .then((docRef) => {
        const syncData = { id: qna.id, ...qna };
        this.sync(syncData);

        return docRef;
      });
  }

  deleteQnA(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    return this.qnasRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  //Reply services
  getReplies(
    qnaId: string,
    privacy?: boolean,
    limit?: number,
    cursor?: any
  ): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${qnaId}/replies`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true);

    ref = ref.where("privacy", "==", privacy);

    ref = ref.orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  getNewReplies(qnaId: string): AngularFirestoreCollection<QnAReply> {
    return this.db.collection(this.dbPath + `/${qnaId}/replies`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .orderBy("createdOn", "desc")
        .limit(10)
    );
  }

  getReply(qnaId: string, id: string) {
    this.replysRef = this.db.collection(this.dbPath + `/${qnaId}/replies`);
    return this.replysRef.doc(id).valueChanges();
  }

  createReply(reply: QnAReply): any {
    reply.author = this.userLogin;
    reply.noLiked = 0;
    reply.isActive = true;
    reply.isDeleted = false;
    reply.createdOn = new Date();
    reply.createdBy = this.userLogin.uid;
    reply.updatedOn = new Date();
    reply.updatedBy = this.userLogin.uid;
    reply.visibility = this.visibility;

    this.replysRef = this.db.collection(
      this.dbPath + `/${reply.qnaId}/replies`
    );

    return this.replysRef.add({ ...reply }).then((docRef) => {
      reply.id = docRef.id;
      return reply;
    });
  }

  updateReply(qnaId: string, id: string, content: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.replysRef = this.db.collection(this.dbPath + `/${qnaId}/replies`);

    return this.replysRef.doc(id).update({
      content: content,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      author: this.userLogin,
    });
  }

  deleteReply(reply: QnAReply): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.replysRef = this.db.collection(
      this.dbPath + `/${reply.qnaId}/replies`
    );

    return this.replysRef.doc(reply.id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      author: this.userLogin,
    });
  }

  sync(content) {
    content.createdOn ? delete content.createdOn : null;
    content.updatedOn ? delete content.updatedOn : null;
    content.content ? delete content.content : null;

    content.tags = JSON.stringify(content.tags);
    content.imagesUrl = JSON.stringify(content.imagesUrl);
    content.fsId = content.id ? content.id : "";

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/QnAs", content).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }
}
